import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';
import { categories } from '@/mocks/categories';
import useSeo from '@/hooks/useSeo';

const genreOptions = ['全部', '剧情', '爱情', '社会', '家庭', '悬疑'];
const ratingOptions = [
  { label: '全部评分', min: 0, max: 10 },
  { label: '8.5 分以上', min: 8.5, max: 10 },
  { label: '8.0 - 8.5 分', min: 8.0, max: 8.5 },
  { label: '7.5 - 8.0 分', min: 7.5, max: 8.0 },
  { label: '7.5 分以下', min: 0, max: 7.5 },
];
const sortOptions = [
  { value: 'latest', label: '最新上架' },
  { value: 'rating', label: '评分最高' },
  { value: 'popular', label: '人气最高' },
];
const PAGE_SIZE = 6;

const catToGenre: Record<string, string> = {
  drama: '剧情',
  romance: '爱情',
  society: '社会',
  family: '家庭',
  suspense: '悬疑',
};

const yearOptions = ['全部', ...Array.from(new Set(movies.map((m) => String(m.year)))).sort((a, b) => Number(b) - Number(a))];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const catParam = searchParams.get('cat') || '';

  const [genre, setGenre] = useState('全部');
  const [year, setYear] = useState('全部');
  const [ratingIndex, setRatingIndex] = useState(0);
  const [sort, setSort] = useState('latest');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    if (catParam && catToGenre[catParam]) {
      setGenre(catToGenre[catParam]);
      setPage(1);
    }
  }, [catParam]);

  const activeCategory = categories.find((c) => c.slug === catParam);

  useSeo({
    title: activeCategory
      ? `${activeCategory.name}电影 - 美国,伊人伦理电影在线观看`
      : '全部影片分类 - 美国伦理电影大全',
    description: activeCategory
      ? `${activeCategory.description} 本分类共收录 ${activeCategory.count} 部精选美国${activeCategory.name}电影，提供剧情介绍与影评。`
      : '浏览全部美国伦理题材电影，可按类型、上映年份与评分区间筛选，快速找到符合你口味的伦理电影。',
    keywords: activeCategory
      ? `${activeCategory.name},${activeCategory.name}电影,美国伦理片,伦理电影推荐`
      : '美国伦理片,伦理电影分类,美国伦理电影,伦理片推荐',
  });

  const filtered = useMemo(() => {
    const r = ratingOptions[ratingIndex];
    const list = movies.filter((m) => {
      const genreOk = genre === '全部' || m.genres.includes(genre);
      const yearOk = year === '全部' || String(m.year) === year;
      const ratingOk = m.rating >= r.min && m.rating <= r.max;
      return genreOk && yearOk && ratingOk;
    });
    if (sort === 'rating') return list.sort((a, b) => b.rating - a.rating);
    if (sort === 'popular') return list.sort((a, b) => b.popularity - a.popularity);
    return list.sort((a, b) => b.addedDate.localeCompare(a.addedDate));
  }, [genre, year, ratingIndex, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetAll = () => {
    setGenre('全部');
    setYear('全部');
    setRatingIndex(0);
    setSort('latest');
    setPage(1);
    setSearchParams({});
  };

  const applyGenre = (g: string) => {
    setGenre(g);
    setPage(1);
    if (catParam) setSearchParams({});
  };

  return (
    <Layout>
      <div className="px-4 md:px-8 pt-6 pb-4">
        <nav className="flex items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">影片分类</span>
          {activeCategory && (
            <>
              <i className="ri-arrow-right-s-line" />
              <span className="text-primary-600">{activeCategory.name}</span>
            </>
          )}
        </nav>
      </div>

      <section className="px-4 md:px-8 pb-6 border-b border-background-200">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
          {activeCategory ? `${activeCategory.name}电影` : '全部美国伦理电影'}
        </h1>
        <p className="mt-2 text-sm leading-7 text-foreground-500 max-w-3xl">
          {activeCategory
            ? activeCategory.description
            : '本站收录的美国伦理题材电影，聚焦剧情、社会与家庭等多元视角。你可以按类型、上映年份与评分区间进行筛选，快速定位想看的内容。'}
        </p>
      </section>

      <div className="px-4 md:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* 筛选侧栏 */}
          <aside className={`lg:w-64 shrink-0 ${filterOpen ? 'block' : 'hidden'} lg:block`}>
            <div className="lg:sticky lg:top-24 rounded-lg bg-background-50 border border-background-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-foreground-950 flex items-center gap-2">
                  <i className="ri-filter-3-line" /> 筛选条件
                </h2>
                <button type="button" onClick={resetAll} className="text-xs text-primary-600 hover:text-primary-700 cursor-pointer">重置</button>
              </div>

              <div className="mb-5">
                <p className="text-xs font-medium text-foreground-700 mb-2.5">影片类型</p>
                <div className="flex flex-wrap gap-2">
                  {genreOptions.map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => applyGenre(g)}
                      className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer ${genre === g ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-secondary-100'}`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <p className="text-xs font-medium text-foreground-700 mb-2.5">上映年份</p>
                <div className="flex flex-wrap gap-2">
                  {yearOptions.map((y) => (
                    <button
                      key={y}
                      type="button"
                      onClick={() => { setYear(y); setPage(1); }}
                      className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer ${year === y ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-secondary-100'}`}
                    >
                      {y === '全部' ? '全部年份' : y}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-foreground-700 mb-2.5">评分区间</p>
                <div className="flex flex-col gap-2">
                  {ratingOptions.map((r, i) => (
                    <button
                      key={r.label}
                      type="button"
                      onClick={() => { setRatingIndex(i); setPage(1); }}
                      className={`px-3 py-1.5 rounded-md text-xs text-left transition-colors cursor-pointer ${ratingIndex === i ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-secondary-100'}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* 列表区 */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
              <p className="text-sm text-foreground-500">
                共找到 <span className="text-foreground-950 font-semibold">{filtered.length}</span> 部影片
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setFilterOpen((v) => !v)}
                  className="lg:hidden px-3 py-2 rounded-md bg-background-50 border border-background-200 text-xs text-foreground-700 flex items-center gap-1.5 cursor-pointer"
                >
                  <i className="ri-filter-3-line" /> 筛选
                </button>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-foreground-500">排序</span>
                  <select
                    value={sort}
                    onChange={(e) => { setSort(e.target.value); setPage(1); }}
                    className="px-3 py-2 rounded-md bg-background-50 border border-background-200 text-xs text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer"
                  >
                    {sortOptions.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {pageItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 rounded-lg bg-background-50 border border-background-200">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl" />
                </span>
                <p className="text-sm text-foreground-600 mb-4">暂无相关影片，试试其他筛选条件</p>
                <button type="button" onClick={resetAll} className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  重置筛选条件
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
                {pageItems.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            )}

            {filtered.length > 0 && totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  type="button"
                  disabled={safePage === 1}
                  onClick={() => setPage(safePage - 1)}
                  className="px-3.5 py-2 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                >
                  上一页
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPage(i + 1)}
                    className={`w-9 h-9 rounded-md text-sm transition-colors cursor-pointer ${safePage === i + 1 ? 'bg-primary-500 text-background-50' : 'bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300'}`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={safePage === totalPages}
                  onClick={() => setPage(safePage + 1)}
                  className="px-3.5 py-2 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                >
                  下一页
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}